import { useApolloClient } from "@apollo/client";
import { RiArticleLine, RiSearchLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement,
    createRef,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Form, ListGroup, Modal } from "react-bootstrap";
import { RiHashtag } from "react-icons/ri";
import {
    Article,
    GetArticleDocument,
    useSearchArticlesQuery,
} from "../../../__generated__/api";
import useKeyboardShortcuts, { useKeyboardState } from "../../../contexts/KeyboardShortcutContext";
import { useTheme } from "../../../contexts/Theme";
import { LoadingIcon } from "../../loading/Loading";
import Popover from "../../popover/Popover";
import Button from "../../theme/button/Button";
import FormGroup from "../../theme/form-group/FormGroup";
import Toggle from "../../theme/toggle/Toggle";
import Styles from "./AnswerFinder.module.scss";
import ArticlePreview, { TicketMessageArticlePreview } from "./ArticlePreview";

const KEYBOARD_CONTEXT = "chat";
const SEARCH_KEYBOARD_CONTEXT = "article-search";

export default function AnswerFinder({
    onAnswerSelected,
    onCannedMessageSelected,
}: {
    onAnswerSelected: (article: TicketMessageArticlePreview) => void,
    onCannedMessageSelected: (body: string | undefined) => void,
}): ReactElement {
    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const theme = useTheme();

    const keyboardState = useKeyboardState();

    const [search, setSearch] = useState("");
    const searchRef = useMemo(() => createRef<HTMLInputElement>(), []);

    const [isCannedMessage, setIsCannedMessage] = useState<boolean>(false);

    const client = useApolloClient();

    const { data: searchData, loading: searchLoading } = useSearchArticlesQuery({
        variables: {
            query: search,
            options: {
                shouldSearchExternal: true,
            },
        },
        skip: search == null || search.length < 2,
    });

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "cmd+3",
            context: KEYBOARD_CONTEXT,
            handler: () => {
                setShouldShow(true);
            },
        });
    });

    const searchResults = searchData?.articles.results ?? [];

    const selected = (selectedIndex >= 0
        ? (selectedIndex % searchResults.length)
        : Math.abs(searchResults.length + ((selectedIndex + 1) % searchResults.length)) - 1);

    const getArticleText = async (id: string) => {
        const results = await client.query({
            query: GetArticleDocument,
            variables: {
                id,
            },
        });
        return results.data.article.body.html;
    };

    const handleSelected = (item: Article) => {
        if (!isCannedMessage) {
            onAnswerSelected({
                id: item.id,
                title: item.title,
                iconUrl: item.iconUrl,
                portalId: item.portalId,
                previewText: item.previewText ?? "",
            });
        } else {
            (async () => {
                const results = await getArticleText(item.id);
                onCannedMessageSelected(results);
            })();
        }
        setShouldShow(false);
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "esc",
            context: SEARCH_KEYBOARD_CONTEXT,
            handler: evt => {
                evt?.stopPropagation();
                setShouldShow(false);
            },
        });

        shortcuts.on({
            shortcut: "down",
            context: SEARCH_KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: evt => {
                evt?.preventDefault();
                setSelectedIndex(curr => curr + 1);
            },
        });
        shortcuts.on({
            shortcut: "up",
            context: SEARCH_KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: evt => {
                evt?.preventDefault();
                setSelectedIndex(curr => curr - 1);
            },
        });
        shortcuts.on({
            shortcut: "enter",
            context: SEARCH_KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: () => {
                const selectedResult = searchResults[selected];
                handleSelected(selectedResult as Article);
            },
        });
    });

    useEffect(() => {
        if (shouldShow && searchRef.current) {
            searchRef.current.selectionStart = 0;
            searchRef.current.selectionEnd = searchRef.current.value.length;
            searchRef.current.focus();
        }
    }, [shouldShow, searchRef]);

    useEffect(() => {
        if (shouldShow) {
            keyboardState.pushContext(SEARCH_KEYBOARD_CONTEXT);
        } else {
            keyboardState.popContext(SEARCH_KEYBOARD_CONTEXT);
        }
    }, [shouldShow]);

    return (
        <>
            <Modal
                show={shouldShow}
                className={Styles["modal-dialog"]}
                backdropClassName={Styles["modal-backdrop"]}
                contentClassName={classNames("rounded shadow border-0", Styles["modal-content"])}
                centered
            >
                <Modal.Body className={classNames(
                    theme.isV3
                        ? Styles["modal-body"]
                        : "overflow-auto flex-grow-1 flex-shrink-1 bg-blue-dark text-white rounded",
                )}
                >
                    <div>
                        <div className={theme.isV3 ? Styles["title-bar"] : "d-flex justify-content-between"}>
                            <small className={theme.isV3 ? Styles.title : "text-blue-light fw-lighter"}>
                                Search for articles
                            </small>
                            <Button
                                size="small"
                                isClose
                                className={!theme.isV3 ? "p-0 text-blue-light mt-n1" : ""}
                                variant="none"
                                onClick={() => setShouldShow(false)}
                            />
                        </div>
                        {theme.isV3 ? (
                            <FormGroup
                                ref={searchRef}
                                type="search"
                                prefixIcon={<RiSearchLine />}
                                placeholder="Search..."
                                size="small"
                                value={search}
                                onChange={evt => setSearch(evt.target.value)}
                            />
                        ) : (
                            <input
                                ref={searchRef}
                                type="search"
                                placeholder="Search..."
                                className="w-100 rounded no-border text-white mt-1"
                                value={search}
                                onChange={evt => setSearch(evt.target.value)}
                            />
                        )}

                        <div className={classNames(!theme.isV3 ? "text-light" : "", "mt-3 d-flex align-items-center")}>
                            {theme.isV3 ? (
                                <>
                                    <Toggle
                                        checked={isCannedMessage}
                                        className="m3e-1"
                                        onChange={() => setIsCannedMessage(!isCannedMessage)}
                                    />
                                    {" "}
                                    Edit before sending
                                </>

                            ) : (
                                <Form.Check
                                    type="switch"
                                    className="fs-6 text-blue-light"
                                    id="edit-before-sending"
                                    checked={isCannedMessage}
                                    onChange={() => setIsCannedMessage(isCanned => !isCanned)}
                                    label="Edit before sending"
                                />
                            )}
                            <Popover
                                buttonClassName={theme.isV3 ? "m3s-1" : "text-blue-light ms-1"}
                                title="Canned Response"
                            >
                                Inserts the article body into the conversation field.
                                Edit before sending to customize the message as needed.
                            </Popover>

                        </div>

                        {searchLoading && (
                            <div className="mt-2 text-center"><LoadingIcon /></div>
                        )}

                        {search && searchResults.isNotEmpty && (
                            <ListGroup variant="flush" className={theme.isV3 ? Styles["list-answers"] : "mt-4"}>
                                {searchResults?.map((item, index) => (
                                    <ListGroup.Item
                                        key={item.id}
                                        className={
                                            theme.isV3
                                                ? Styles["list-item"]
                                                : classNames(
                                                    "border-bottom-0 my-1 cursor-pointer",
                                                    index === selected ? "bg-light text-black" : "bg-blue-dark text-light",
                                                // eslint-disable-next-line react/jsx-indent
                                                )
                                        }
                                        onClick={() => handleSelected(item as Article)}
                                    >
                                        <ArticlePreview article={item as TicketMessageArticlePreview} />
                                    </ListGroup.Item>

                                ))}
                            </ListGroup>
                        )}
                        {!searchLoading && search && searchResults?.isEmpty && (
                            <div className="w-100 text-center mt-3">
                                <small className={theme.isV3 ? Styles["no-matches"] : "text-light"}>
                                    Sorry, no matching articles.
                                </small>
                            </div>
                        )}
                    </div>
                </Modal.Body>

            </Modal>
            {theme.isV3 ? (
                <Button
                    prefixIcon={<RiArticleLine />}
                    size="small"
                    className="toolbar-action"
                    variant="ghost-gray"
                    onClick={() => setShouldShow(true)}
                />
            ) : (
                <button
                    type="button"
                    className="btn btn-none p-1 text-gray-500 pe-1 "
                    onClick={() => setShouldShow(true)}
                    aria-label="Click to find an article to insert"
                >
                    <RiHashtag />
                </button>
            )}

        </>

    );
}
